import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Box, useMediaQuery, useTheme, IconButton, Typography } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import ShortStories from './components/ShortStory/ShortStories';
import ShortStoryDetail from './components/ShortStory/ShortStoryDetail';
import AlgorithmicProblems from './components/AlgorithmicProblem/AlgorithmicProblems';
import AlgorithmicProblemDetail from './components/AlgorithmicProblem/AlgorithmicProblemDetail';
import Profile from './components/Navigation/Profile';
import GrammarTopic from './pages/GrammarTopic';
import Flashcards from './components/Learning/Flashcards';
import LongPosts from './components/LongPost/LongPosts';
import LongPostDetail from './components/LongPost/LongPostDetail';
import VideoPostsList from './components/VideoPost/VideoPostsList';
import VideoPostDetail from './components/VideoPost/VideoPostDetail';
import Bookmarks from './components/Navigation/Bookmarks';
import Settings from './components/Navigation/Settings';
import Help from './components/Navigation/Help';
import Watch from './components/VideoPost/Watch';
import Shorts from './components/VideoPost/Shorts';
import PlaylistViewer from './components/Playlist/PlaylistViewer';
import News from './components/News/News';
import Education from './components/Education/Education';

import Login from './Auth/Login';
import { history } from './_helpers';
import { HomeBar, Sidebar } from './components/Navigation';
import { PrivateRoute, ErrorBoundary } from './components/Common';
import { ThemeProvider } from './contexts/ThemeContext';
import { useTheme as useAppTheme } from './contexts/ThemeContext';
import logger from './services/logger';
import './services/apiLogger'; // Initialize API logging

// Collapsed Brand Component - shows when sidebar is collapsed
const CollapsedBrand = ({ onClick, sidebarOpen, sidebarCollapsed }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: authUser } = useSelector(x => x.auth);
  
  // Don't show on mobile, when sidebar is open, or when not authenticated
  if (isMobile || sidebarOpen || !authUser) return null;
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: 16,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        p: 1.5,
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
          opacity: 0.9
        }
      }}
      onClick={onClick}
    >
      <img 
        src="/icon.png" 
        alt="TilBuzz Logo" 
        style={{ 
          width: '24px', 
          height: '24px', 
          marginRight: '8px',
          borderRadius: '4px'
        }} 
      />
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'monospace',
          fontWeight: 700,
          letterSpacing: '.1rem',
          color: 'primary.main',
          fontSize: '1rem'
        }}
      >
        TilBuzz
      </Typography>
    </Box>
  );
};

// Main App component that uses the theme context
function AppContent() {
  // init custom history object to allow navigation from 
  // anywhere in the react app (inside or outside components)
  history.navigate = useNavigate();
  history.location = useLocation();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { darkMode, toggleDarkMode } = useAppTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get authentication state
  const { user: authUser } = useSelector(x => x.auth);
  
  // Close sidebar on mobile by default
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const handleSidebarToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleBrandClick = () => {
    // Toggle sidebar open when brand is clicked
    if (!isMobile) {
      setSidebarOpen(!sidebarOpen);
      if (!sidebarOpen) {
        setSidebarCollapsed(false); // When opening, don't keep it collapsed
      }
    }
  };

  useEffect(() => {
    // Log application startup
    logger.info('Application Started', {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      darkMode
    });

    // Log component mount
    logger.logComponentMount('App');

    return () => {
      // Log component unmount
      logger.logComponentUnmount('App');
    };
  }, [darkMode]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {authUser && (
        <>
          <ErrorBoundary name="Sidebar">
            <Sidebar
              open={sidebarOpen}
              collapsed={sidebarCollapsed}
              onClose={handleSidebarClose}
              onToggle={handleSidebarToggle}
              darkMode={darkMode}
              onDarkModeToggle={toggleDarkMode}
            />
          </ErrorBoundary>

          {/* Collapsed Brand - shows when sidebar is collapsed */}
          <CollapsedBrand 
            onClick={handleBrandClick}
            sidebarOpen={sidebarOpen}
            sidebarCollapsed={sidebarCollapsed}
          />
        </>
      )}

      {/* Mobile Menu Button - shown on all pages except home when authenticated */}
      {authUser && isMobile && location.pathname !== '/' && (
        <Box
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
          }}
        >
          <IconButton
            color="primary"
            aria-label="open menu"
            onClick={handleSidebarToggle}
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'background.paper',
                boxShadow: 4,
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        {/* HomeBar - only rendered inside main content on home page */}
        {authUser && location.pathname === '/' && (
          <ErrorBoundary name="HomeBar">
            <HomeBar 
              onSidebarToggle={handleSidebarToggle}
              onBrandClick={handleBrandClick}
            />
          </ErrorBoundary>
        )}

        <Routes>
          {/* Home route redirects to the current profile as dashboard */}
          <Route
            exact
            path="/"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Profile">
                  <Profile />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          
          {/* Profile route (will be the new home dashboard) */}
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Profile">
                  <Profile />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />

          {/* New menu routes */}
          <Route
            path="/bookmarks"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Bookmarks">
                  <Bookmarks />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Settings">
                  <Settings />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          
          <Route
            path="/help"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Help">
                  <Help />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          
          <Route
            path="/watch"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Watch">
                  <Watch />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          
          <Route
            path="/shorts"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Shorts">
                  <Shorts />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />

          {/* News and Education routes */}
          <Route
            path="/news"
            element={
              <PrivateRoute>
                <ErrorBoundary name="News">
                  <News />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          
          <Route
            path="/education"
            element={
              <PrivateRoute>
                <ErrorBoundary name="Education">
                  <Education />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />

          {/* Existing routes */}
          <Route path="/login" element={
            <ErrorBoundary name="Login">
              <Login />
            </ErrorBoundary>
          } />
          
          <Route path='/short-stories' element={
            <PrivateRoute>
              <ErrorBoundary name="ShortStories">
                <ShortStories />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/story/:id' element={
            <PrivateRoute>
              <ErrorBoundary name="ShortStoryDetail">
                <ShortStoryDetail />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/algorithmic-problems' element={
            <PrivateRoute>
              <ErrorBoundary name="AlgorithmicProblems">
                <AlgorithmicProblems />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/algorithmic-problem/:id' element={
            <PrivateRoute>
              <ErrorBoundary name="AlgorithmicProblemDetail">
                <AlgorithmicProblemDetail />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/grammar/topic/:slug' element={
            <PrivateRoute>
              <ErrorBoundary name="GrammarTopic">
                <GrammarTopic />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/flashcards' element={
            <PrivateRoute>
              <ErrorBoundary name="Flashcards">
                <Flashcards />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/flashcards/:storyId' element={
            <PrivateRoute>
              <ErrorBoundary name="FlashcardsWithStory">
                <Flashcards />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/long-posts' element={
            <PrivateRoute>
              <ErrorBoundary name="LongPosts">
                <LongPosts />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/long-posts/:slug' element={
            <PrivateRoute>
              <ErrorBoundary name="LongPostDetail">
                <LongPostDetail />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/video-posts' element={
            <PrivateRoute>
              <ErrorBoundary name="VideoPostsList">
                <VideoPostsList />
              </ErrorBoundary>
            </PrivateRoute>
          } />
          
          <Route path='/video-posts/:id' element={
            <PrivateRoute>
              <ErrorBoundary name="VideoPostDetail">
                <VideoPostDetail />
              </ErrorBoundary>
            </PrivateRoute>
          } />

          {/* Playlist routes */}
          <Route path='/playlists/:slug' element={
            <PrivateRoute>
              <ErrorBoundary name="PlaylistViewer">
                <PlaylistViewer />
              </ErrorBoundary>
            </PrivateRoute>
          } />
        </Routes>
      </Box>
    </Box>
  );
}

// Wrapper component with ThemeProvider
function App() {
  return (
    <ErrorBoundary name="App">
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
